import { ACCOUNT_APPROVAL_TIME, COMPANY, CONTACT_EMAIL } from '../../components/landing/landingFacts';

/**
 * Privacy, cookie and terms pages. DRAFT until reviewed by the company's legal advisor:
 * while LEGAL_DRAFT is true every page shows a "bozza in revisione" notice.
 * Facts come from docs/architecture/server-data-retention.md and the API code; keep them in sync.
 */
export const LEGAL_DRAFT = true;
export const LEGAL_UPDATED = '13 ottobre 2026';

export type LegalBlock = string | { list: string[] };
export type LegalSection = { heading: string; blocks: LegalBlock[] };
export type LegalDocument = { title: string; description: string; intro: string; sections: LegalSection[] };
export type LegalDocumentKey = 'privacy' | 'cookie' | 'termini';

const company = `${COMPANY.name}, ${COMPANY.address}, P.IVA ${COMPANY.vatNumber}, R.E.A. n. ${COMPANY.rea}`;

export const legalDocuments: Record<LegalDocumentKey, LegalDocument> = {
    privacy: {
        title: 'Informativa sulla privacy',
        description: 'Come Semplora tratta i dati personali di chi usa il portale: quali dati, perché, per quanto tempo e con quali fornitori.',
        intro: 'Questa informativa spiega come trattiamo i dati personali di chi visita il sito e usa il portale Semplora, ai sensi degli articoli 13 e 14 del Regolamento (UE) 2016/679 (GDPR).',
        sections: [
            {
                heading: 'Titolare del trattamento',
                blocks: [`Il titolare del trattamento è ${company}. Per qualsiasi richiesta sui tuoi dati puoi scrivere a ${CONTACT_EMAIL}.`],
            },
            {
                heading: 'Quali dati trattiamo',
                blocks: [
                    { list: [
                        'Dati dell’account: indirizzo email, nome visualizzato, foto del profilo, provider usato per l’accesso (Microsoft, Google o GitHub) e identificativo dell’account di autenticazione.',
                        'Dati del profilo che scegli di indicare: nome e cognome, azienda, telefono, indirizzo, lingua e tema preferiti.',
                        'Dati di gestione dell’accesso: stato di approvazione dell’account, ruolo, strumenti abilitati, data di creazione e dell’ultimo accesso.',
                        'Dati di utilizzo e diagnostica: quali strumenti usi ed eventuali errori tecnici, per far funzionare e migliorare il servizio.',
                        'Dati prodotti dagli strumenti che attivi: per esempio le fotografie dei metadati di Environment Drift Monitor, le scansioni di Data Quality Assistant e i report programmati.',
                    ] },
                    'Le connessioni ai tuoi ambienti Dynamics 365 sono salvate cifrate nel tuo browser. Il server ne riceve una copia solo in memoria, per la durata della sessione e al massimo per 8 ore, e non le salva nel database. Lo stesso vale per le chiavi dei provider di intelligenza artificiale: restano cifrate nel browser e accompagnano la singola richiesta.',
                ],
            },
            {
                heading: 'I dati dei tuoi ambienti Dynamics 365',
                blocks: [
                    'Quando colleghi un ambiente, Semplora legge o modifica i dati e i metadati di quell’ambiente solo per eseguire le operazioni che avvii tu. Per questi dati il titolare del trattamento è l’organizzazione a cui appartiene l’ambiente; Nauka Team li tratta per suo conto e secondo le sue istruzioni.',
                ],
            },
            {
                heading: 'Perché li trattiamo e su quale base',
                blocks: [
                    { list: [
                        'Fornire il portale e gli strumenti che usi, gestire l’account e la sua approvazione: esecuzione del contratto (art. 6.1.b GDPR).',
                        'Inviarti comunicazioni di servizio, come l’email che conferma l’attivazione dell’account o i report che hai programmato: esecuzione del contratto.',
                        'Proteggere il servizio, individuare errori e abusi: legittimo interesse (art. 6.1.f GDPR).',
                        'Adempiere agli obblighi di legge: obbligo legale (art. 6.1.c GDPR).',
                    ] },
                    'Non usiamo i tuoi dati per pubblicità né per profilazione.',
                ],
            },
            {
                heading: 'Fornitori che trattano i dati per nostro conto',
                blocks: [
                    { list: [
                        'Microsoft: infrastruttura cloud Azure (il server applicativo si trova nella region Italy North) e accesso con account Microsoft.',
                        'Google: Firebase Authentication per l’accesso.',
                        'GitHub: accesso con account GitHub, se scegli questo provider.',
                        'Il fornitore del servizio di posta elettronica usato per inviare le comunicazioni di servizio.',
                        'Provider di intelligenza artificiale (per esempio Anthropic o OpenAI): solo quando usi una funzione IA con la tua configurazione. Il contenuto della richiesta va al provider che hai scelto, secondo le sue condizioni.',
                    ] },
                    'Alcuni fornitori possono trattare dati fuori dall’Unione europea. In questi casi il trasferimento avviene sulla base delle garanzie previste dal GDPR, come l’EU-U.S. Data Privacy Framework o le clausole contrattuali standard della Commissione europea.',
                ],
            },
            {
                heading: 'Per quanto tempo li conserviamo',
                blocks: [
                    { list: [
                        'Dati dell’account e del profilo: finché l’account è attivo. Puoi chiederne la cancellazione in qualsiasi momento.',
                        'Eventi di diagnostica: 30 giorni.',
                        'Fotografie dei metadati di Environment Drift Monitor: 180 giorni.',
                        'File temporanei e risultati delle operazioni sul server: al massimo un’ora dalla fine dell’operazione.',
                        'Connessioni di sessione in memoria: al massimo 8 ore.',
                    ] },
                ],
            },
            {
                heading: 'I tuoi diritti',
                blocks: [
                    `Puoi chiedere l’accesso ai tuoi dati, la rettifica, la cancellazione, la limitazione del trattamento, la portabilità e opporti al trattamento basato sul legittimo interesse, scrivendo a ${CONTACT_EMAIL}. Hai anche il diritto di presentare un reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).`,
                ],
            },
            {
                heading: 'Modifiche a questa informativa',
                blocks: ['Se cambiamo il modo in cui trattiamo i dati aggiorniamo questa pagina e la data in alto. Le modifiche importanti le comunichiamo anche nel portale.'],
            },
        ],
    },
    cookie: {
        title: 'Cookie e memorizzazione nel browser',
        description: 'Semplora non usa cookie di profilazione né strumenti di analisi. Quali dati tecnici salva il browser e perché.',
        intro: 'Semplora non usa cookie di profilazione, pubblicità o strumenti di analisi delle visite. Per questo non ti chiediamo il consenso all’ingresso nel sito.',
        sections: [
            {
                heading: 'Cosa salva il tuo browser',
                blocks: [
                    'Per funzionare il portale usa solo strumenti tecnici di memorizzazione del browser (local storage, session storage e IndexedDB):',
                    { list: [
                        'la sessione di accesso e il token del portale, gestiti da Firebase Authentication e, per gli account Microsoft, dalla libreria Microsoft MSAL;',
                        'il vault cifrato con le connessioni ai tuoi ambienti Dynamics 365 e le impostazioni dei provider di intelligenza artificiale;',
                        'le tue preferenze, come tema, lingua e zoom, e lo stato degli strumenti, come le ultime query e le impostazioni di ciascun tool.',
                    ] },
                    'Questi strumenti sono necessari al servizio che hai richiesto e, secondo l’articolo 122 del Codice privacy e le linee guida del Garante del 10 giugno 2021, non richiedono consenso.',
                ],
            },
            {
                heading: 'Caratteri del sito',
                blocks: ['Il carattere Inter è ospitato sui nostri server: per mostrare il testo il sito non contatta servizi esterni.'],
            },
            {
                heading: 'Come gestirli',
                blocks: ['Puoi cancellare i dati salvati dalle impostazioni del browser. Così però esci dal portale e perdi le connessioni salvate su quel browser, che dovrai inserire di nuovo.'],
            },
            {
                heading: 'Domande',
                blocks: [`Per qualsiasi domanda scrivi a ${CONTACT_EMAIL}. Per sapere come trattiamo i dati personali leggi l’informativa sulla privacy.`],
            },
        ],
    },
    termini: {
        title: 'Termini di servizio',
        description: 'Le condizioni per usare il portale Semplora e i suoi strumenti per Dynamics 365, Dataverse e Power Platform.',
        intro: `Questi termini regolano l’uso del portale Semplora, fornito da ${company}. Accedendo al portale li accetti.`,
        sections: [
            {
                heading: 'Il servizio',
                blocks: ['Semplora è un portale web con strumenti per lavorare con Microsoft Dynamics 365, Dataverse e Power Platform. Alcune funzioni richiedono il Desktop Agent, un’applicazione da installare sul tuo computer. Le eventuali condizioni economiche sono concordate separatamente.'],
            },
            {
                heading: 'Account e approvazione',
                blocks: [
                    `Accedi con un account Microsoft, Google o GitHub. Dopo il primo accesso verifichiamo la richiesta, di solito entro ${ACCOUNT_APPROVAL_TIME}, e ti avvisiamo via email quando l’account è attivo. Possiamo rifiutare una richiesta o sospendere un account che viola questi termini.`,
                    'Sei responsabile dell’account che usi per accedere e di quello che fai con il portale.',
                ],
            },
            {
                heading: 'Uso corretto',
                blocks: [
                    { list: [
                        'Collega solo ambienti per cui hai l’autorizzazione dell’organizzazione che li possiede.',
                        'Rispetta le leggi e le condizioni dei servizi Microsoft che usi tramite Semplora.',
                        'Non tentare di aggirare la sicurezza del portale, di accedere a dati di altri utenti o di sovraccaricare il servizio.',
                    ] },
                ],
            },
            {
                heading: 'Operazioni sui tuoi ambienti',
                blocks: ['Molti strumenti modificano dati e metadati: importazioni, operazioni massive, rilasci di solution, registrazione di plugin. Semplora esegue le operazioni che avvii tu. Prima di confermarle controlla le anteprime, prova sugli ambienti di test e assicurati di avere i backup che ti servono.'],
            },
            {
                heading: 'Intelligenza artificiale',
                blocks: ['Le risposte generate dall’intelligenza artificiale possono essere incomplete o sbagliate: verificale prima di usarle. I provider IA che scegli applicano le proprie condizioni.'],
            },
            {
                heading: 'Disponibilità e modifiche',
                blocks: ['Lavoriamo perché il servizio sia sempre disponibile, ma possono esserci interruzioni per manutenzione o problemi tecnici. Possiamo aggiungere, cambiare o togliere funzioni. Se modifichiamo questi termini aggiorniamo questa pagina e la data in alto.'],
            },
            {
                heading: 'Responsabilità',
                blocks: ['Il servizio è fornito così com’è. Salvo i casi di dolo o colpa grave e quanto previsto da norme inderogabili, Nauka Team non risponde dei danni indiretti, né della perdita di dati negli ambienti collegati causata da operazioni avviate dall’utente.'],
            },
            {
                heading: 'Proprietà intellettuale e dati',
                blocks: ['Il software, il marchio Semplora e i contenuti del portale appartengono a Nauka Team. I dati dei tuoi ambienti restano tuoi o della tua organizzazione. Per i dati personali vale l’informativa sulla privacy.'],
            },
            {
                heading: 'Legge applicabile e foro',
                blocks: ['Questi termini sono regolati dalla legge italiana. Per ogni controversia è competente il foro di Milano, salvo i casi in cui la legge preveda un foro inderogabile a tutela del consumatore.'],
            },
            {
                heading: 'Contatti',
                blocks: [`Per qualsiasi domanda scrivi a ${CONTACT_EMAIL}.`],
            },
        ],
    },
};
